import { useState } from "react";
import { Sun, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Section } from "@/components/editor/Section";
import { AssetList } from "@/components/editor/AssetList";
import { api, formatApiError } from "@/lib/api";

const PRESETS = [
  { id: "leak", name: "Golden light leak", note: "Warm drifting film-burn glow", bg: "radial-gradient(ellipse at 30% 40%,#FFB45A,#7A3A0A 55%,#000)" },
  { id: "flare", name: "Lens flare", note: "Anamorphic streak gliding across", bg: "linear-gradient(180deg,#000 40%,#BFE0FF 49%,#fff 50%,#BFE0FF 51%,#000 60%)" },
  { id: "starburst", name: "Starburst", note: "Pulsing celestial star rays", bg: "repeating-conic-gradient(from 0deg at 50% 50%,#FFF1C9 0 4deg,#000 4deg 22.5deg)" },
  { id: "dust", name: "Celestial dust", note: "Twinkling golden particles", bg: "radial-gradient(#FFE0A0 1px,transparent 1.5px) 0 0/9px 9px,#05060A" },
];

export const LightPanel = ({ projectId, assets, onCreated, onAdd }) => {
  const [picked, setPicked] = useState("leak");
  const [duration, setDuration] = useState(6);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/generate/light", { project_id: projectId, preset: picked, duration });
      onCreated(data);
      toast.success(`Rendering "${data.name}" — ready in a few seconds`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="light-panel">
      <Section title="Light overlays">
        <div className="grid grid-cols-2 gap-2">
          {PRESETS.map((p) => (
            <button key={p.id} onClick={() => setPicked(p.id)} data-testid={`light-preset-${p.id}`}
              className={`text-left rounded-md overflow-hidden bg-[#0B0C10] border transition-[border-color,box-shadow] ${picked === p.id ? "border-[#FFB800] shadow-[0_0_14px_#FFB80044]" : "border-[#2A2E3D] hover:border-[#3F455A]"}`}>
              <div className="h-14" style={{ background: p.bg }} />
              <div className="p-2"><p className="text-[11px] font-medium leading-tight">{p.name}</p><p className="text-[10px] text-slate-500 mt-0.5">{p.note}</p></div>
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-slate-300"><span>Length</span>
          <div className="flex gap-1">{[4, 6, 10].map((d) => (
            <button key={d} onClick={() => setDuration(d)} data-testid={`light-duration-${d}`}
              className={`px-2.5 py-1 rounded text-[11px] ${duration === d ? "bg-[#FFB800] text-[#0B0C10] font-semibold" : "bg-[#0B0C10] text-slate-400"}`}>{d} s</button>))}
          </div>
        </div>
        <Button onClick={create} disabled={busy} className="w-full mt-3 bg-[#FFB800] hover:bg-[#ffc933] text-[#0B0C10]" data-testid="light-create-button">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sun className="w-4 h-4 mr-2" />}Create overlay
        </Button>
        <p className="text-[11px] text-slate-500 mt-2">Rendered instantly on our servers — no AI credits. Overlays are screen-blended on the V2 layer track over any shot; select one to set opacity, fades, position and motion.</p>
      </Section>
      <Section title="Your light overlays">
        <AssetList assets={assets.filter((a) => a.kind === "vfx" && a.meta?.light)} empty="Created overlays appear here." onAdd={onAdd} />
      </Section>
    </div>
  );
};
